import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * WHERE THE DETAILED BREAKDOWN PAGE IS LOOKING, in its address.
 *
 * `/elements?view=breakdown|execution|resources|plan&scene=<id>&req=<id>`. Every
 * state worth sharing has an address (redesign §2): a link from the plan's
 * overview opens the scene with the requirement in view, and Back returns to the
 * overview it came from. `view` is read only while the production plan is on;
 * `scene` is the page's own, plan or not.
 */
export type PlanView = 'breakdown' | 'execution' | 'resources' | 'plan';

export interface PlanAddress {
  readonly view: PlanView;
  readonly scene: string | null;
  readonly req: string | null;
}

export type PlanAddressPatch = Partial<PlanAddress>;

export interface PlanNavigateOptions {
  /** A new history entry, so Back returns here. Absent: the entry is replaced. */
  readonly push?: boolean;
}

export type PlanNavigate = (patch: PlanAddressPatch, options?: PlanNavigateOptions) => void;

export const DEFAULT_PLAN_ADDRESS: PlanAddress = { view: 'breakdown', scene: null, req: null };

export function parsePlanView(raw: string | null): PlanView {
  return raw === 'execution' || raw === 'resources' || raw === 'plan' ? raw : 'breakdown';
}

export function readPlanAddress(params: URLSearchParams): PlanAddress {
  const scene = params.get('scene');
  const req = params.get('req');

  return {
    view: parsePlanView(params.get('view')),
    scene: scene === null || scene === '' ? null : scene,
    req: req === null || req === '' ? null : req,
  };
}

/** The address with a patch applied; every other parameter is kept as it was. */
export function writePlanAddress(params: URLSearchParams, patch: PlanAddressPatch): URLSearchParams {
  const next = new URLSearchParams(params);

  if (patch.view !== undefined) next.set('view', patch.view);
  for (const key of ['scene', 'req'] as const) {
    const value = patch[key];
    if (value === undefined) continue;
    if (value === null) next.delete(key);
    else next.set(key, value);
  }

  return next;
}

/** The page's address, read and written through the router — one write per change. */
export function usePlanAddress(): [PlanAddress, PlanNavigate] {
  const [params, setParams] = useSearchParams();
  const address = useMemo(() => readPlanAddress(params), [params]);
  const navigate = useCallback<PlanNavigate>(
    (patch, options) => {
      setParams((current) => writePlanAddress(current, patch), { replace: options?.push !== true });
    },
    [setParams],
  );

  return [address, navigate];
}
